import dayjs from "dayjs";
import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import styled, { css } from "styled-components";

import WideDefaultLayout from "@layouts/WideDefaultLayout";

import { showLoaCalendar } from "@core/atoms/todo.atom";
import useGetLogsProfit from "@core/hooks/queries/logs/useGetLogsProfit";
import { useSchedulesMonth } from "@core/hooks/queries/schedule";
import useIsGuest from "@core/hooks/useIsGuest";
import useModalState from "@core/hooks/useModalState";
import type { ScheduleCategory, ScheduleItem } from "@core/types/schedule";
import { getWeekdayNumber } from "@core/utils";

import { MdSearch } from "@react-icons/all-files/md/MdSearch";

import Button from "@components/Button";
import Modal from "@components/Modal";
import SortedScheduleList from "@components/SortedScheduleList";

import ArrowIcon from "@assets/images/ico_cal_arr.svg";

import FormModal from "./components/FormModal";
import SearchResultsList from "./components/SearchResultsList";

const MAX_COMPACT_DESKTOP = 5;
const MAX_COMPACT_MOBILE = 3;

const useIsMobile = (breakpoint = 600) => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);

  return isMobile;
};

interface DayDetail {
  label: string;
  schedules: ScheduleItem[];
}

const ScheduleIndex = () => {
  const isGuest = useIsGuest();
  const isMobile = useIsMobile();
  const maxCompact = isMobile ? MAX_COMPACT_MOBILE : MAX_COMPACT_DESKTOP;
  const [createModal, setCreateModal] = useModalState<boolean>();
  const [targetSchedule, setTargetSchedule] = useModalState<ScheduleItem>();
  const [dayDetail, setDayDetail] = useState<DayDetail | undefined>();
  const [showWen, setShowWen] = useAtom(showLoaCalendar);
  const today = useMemo(() => dayjs(), []);
  const [filter, setFilter] = useState<ScheduleCategory | "ALL">("ALL");
  const [startDate, setStartDate] = useState(today.startOf("month"));
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput), 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const getSchedules = useSchedulesMonth({
    year: startDate.year(),
    month: startDate.month() + 1,
    query: searchQuery || undefined,
  });

  const { data: profitData = [] } = useGetLogsProfit({
    startDate: startDate.format("YYYY-MM-DD"),
    endDate: startDate.endOf("month").format("YYYY-MM-DD"),
  });

  const monthlyProfit = useMemo(() => {
    return profitData.reduce((acc, curr) => acc + curr.totalProfit, 0);
  }, [profitData]);

  const dailyProfitMap = useMemo(() => {
    const map = new Map<string, number>();
    profitData.forEach((item) => {
      map.set(item.localDate, item.totalProfit);
    });
    return map;
  }, [profitData]);

  const handleChangeWen = useCallback((): void => {
    setShowWen(!showWen);
  }, [setShowWen, showWen]);

  const handleClickScheduleItem = useCallback(
    (schedule: ScheduleItem) => {
      if (isGuest) {
        toast.warn("테스트 계정은 이용하실 수 없습니다.");
      } else {
        setTargetSchedule(schedule);
      }
    },
    [isGuest, setTargetSchedule]
  );

  const weekdays = useMemo(
    () =>
      showWen
        ? ["수", "목", "금", "토", "일", "월", "화"]
        : ["일", "월", "화", "수", "목", "금", "토"],
    [showWen]
  );

  const startOffset = useMemo(
    () =>
      showWen
        ? (startDate.startOf("month").day() + 4) % 7
        : startDate.startOf("month").day(),
    [showWen, startDate]
  );

  const prevMonthDays = useMemo(() => {
    if (startOffset === 0) return [];
    const prevMonth = startDate.subtract(1, "month");
    const lastDate = prevMonth.daysInMonth();
    return Array.from({ length: startOffset }, (_, i) => ({
      day: lastDate - startOffset + i + 1,
      month: prevMonth.format("M"),
    }));
  }, [startOffset, startDate]);

  const totalDays = startOffset + startDate.daysInMonth();
  const nextMonthDays = useMemo(() => {
    const remaining = totalDays % 7 === 0 ? 0 : 7 - (totalDays % 7);
    const nextMonth = startDate.add(1, "month");
    return Array.from({ length: remaining }, (_, i) => ({
      day: i + 1,
      month: nextMonth.format("M"),
    }));
  }, [totalDays, startDate]);

  const isSearching = !!searchQuery;

  const schedulesGroupedByDate = useMemo(() => {
    const schedules = getSchedules.data ?? [];
    const filtered =
      filter === "ALL"
        ? schedules
        : schedules.filter((item) => item.scheduleCategory === filter);

    const map = new Map<string, ScheduleItem[]>();

    Array.from({ length: startDate.daysInMonth() }, (_, i) => i).forEach(
      (i) => {
        const date = startDate.add(i, "day");
        const dateKey = date.format("YYYY-MM-DD");
        const weekday = showWen ? (date.day() + 4) % 7 : date.day();

        const daySchedules = filtered.filter((item) => {
          const scheduleWeekday = getWeekdayNumber(item.dayOfWeek);
          const adjustedWeekday = showWen
            ? (scheduleWeekday + 4) % 7
            : scheduleWeekday;

          if (item.repeatWeek) {
            return adjustedWeekday === weekday;
          }
          if (item.date) {
            return dayjs(item.date).isSame(date, "date");
          }
          return false;
        });

        map.set(dateKey, daySchedules);
      }
    );

    return map;
  }, [getSchedules.data, filter, startDate, showWen]);

  return (
    <WideDefaultLayout pageTitle="일정">
      <Wrapper>
        {/* 검색 바 */}
        <TopSearchBar>
          <SearchIcon>
            <MdSearch size="18" />
          </SearchIcon>
          <SearchInput
            type="text"
            placeholder="레이드명 또는 메모 검색"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <ClearButton onClick={() => { setSearchInput(""); setSearchQuery(""); }}>
              ✕
            </ClearButton>
          )}
        </TopSearchBar>

        <Controller>
          <button
            type="button"
            onClick={() => setStartDate(startDate.subtract(1, "month"))}
          >
            <span className="text-hidden">이전</span>
          </button>
          <div>
            <strong>{startDate.format("YYYY년 MM월")}</strong>
            <p style={{ textAlign: "center", fontSize: "14px" }}>
              (월간 수익: {monthlyProfit.toLocaleString()} G)
            </p>
          </div>
          <button
            type="button"
            className="next"
            onClick={() => setStartDate(startDate.add(1, "month"))}
          >
            <span className="text-hidden">다음</span>
          </button>
        </Controller>

        <HeaderGroup>
          <Filters>
            <Button
              css={filterButtonCss(filter === "ALL")}
              onClick={() => setFilter("ALL")}
            >
              모든 일정
            </Button>
            <Button
              css={filterButtonCss(filter === "ALONE")}
              onClick={() => setFilter("ALONE")}
            >
              내 일정
            </Button>
            <Button
              css={filterButtonCss(filter === "PARTY")}
              onClick={() => setFilter("PARTY")}
            >
              깐부 일정
            </Button>
          </Filters>
          <Buttons>
            <CalendarSwitchContainer>
              <span>{showWen ? `로아달력` : `일반달력`}</span>
              <SwitchWrapper $isOn={showWen} onClick={handleChangeWen}>
                <SwitchSlider $isOn={showWen} />
              </SwitchWrapper>
            </CalendarSwitchContainer>
            <Button size="large" onClick={() => setCreateModal(true)}>
              일정 추가
            </Button>
          </Buttons>
        </HeaderGroup>

        {isSearching ? (
          <SearchResultsList
            data={getSchedules.data ?? []}
            filter={filter}
            onClickItem={handleClickScheduleItem}
          />
        ) : (
          <MonthGrid>
            {weekdays.map((day, index) => (
              <WeekdayHeader key={index} $index={index} $showWen={showWen}>
                {day}
              </WeekdayHeader>
            ))}

            {prevMonthDays.map(({ day, month }, index) => (
              <DateCell key={`prev-${index}`} $isPrevOrNext>
                <DateHeader>
                  <DateNumber $isPrevOrNext>{month}/{day}</DateNumber>
                </DateHeader>
              </DateCell>
            ))}

            {[...Array(startDate.daysInMonth())].map((_, index) => {
              const date = startDate.add(index, "day");
              const weekday = showWen ? (date.day() + 4) % 7 : date.day();
              const isToday = date.isSame(dayjs(), "date");
              const dateKey = date.format("YYYY-MM-DD");
              const daySchedules =
                schedulesGroupedByDate.get(dateKey) ?? [];

              const sorted = [...daySchedules].sort((a, b) =>
                a.time < b.time ? -1 : a.time > b.time ? 1 : 0
              );
              const visible = sorted.slice(0, maxCompact);
              const hiddenCount = sorted.length - maxCompact;

              return (
                <DateCell
                  key={index}
                  $weekday={weekday}
                  $isToday={isToday}
                  $showWen={showWen}
                >
                  <DateHeader>
                    <DateNumber $isToday={isToday} $weekday={weekday} $showWen={showWen}>
                      {date.format("D")}
                    </DateNumber>
                    {dailyProfitMap.has(dateKey) && (
                      <Profit>
                        {dailyProfitMap.get(dateKey)?.toLocaleString()} G
                      </Profit>
                    )}
                  </DateHeader>

                  <ScheduleArea>
                    {visible.map((item) => (
                      <CompactItem
                        key={item.scheduleId}
                        onClick={() => handleClickScheduleItem(item)}
                        $isAlone={item.scheduleCategory === "ALONE"}
                      >
                        <CompactTime>
                          {dayjs(`2000-01-01 ${item.time}`).format("HH:mm")}
                        </CompactTime>
                        <CompactName>{item.raidName}</CompactName>
                      </CompactItem>
                    ))}
                    {hiddenCount > 0 && (
                      <MoreCount
                        onClick={() =>
                          setDayDetail({
                            label: date.format("M월 D일"),
                            schedules: daySchedules,
                          })
                        }
                      >
                        +{hiddenCount}건
                      </MoreCount>
                    )}
                  </ScheduleArea>
                </DateCell>
              );
            })}

            {nextMonthDays.map(({ day, month }, index) => (
              <DateCell key={`next-${index}`} $isPrevOrNext>
                <DateHeader>
                  <DateNumber $isPrevOrNext>{month}/{day}</DateNumber>
                </DateHeader>
              </DateCell>
            ))}
          </MonthGrid>
        )}
      </Wrapper>

      <FormModal
        isOpen={!!createModal || targetSchedule !== undefined}
        targetSchedule={targetSchedule}
        onClose={() => {
          if (createModal) {
            setCreateModal();
          } else {
            setTargetSchedule();
          }
        }}
        year={startDate.year()}
        month={startDate.month() + 1}
      />

      <Modal
        title={dayDetail?.label}
        isOpen={!!dayDetail}
        onClose={() => setDayDetail(undefined)}
      >
        <DayDetailList>
          <SortedScheduleList
            onClickScheduleItem={(schedule) => {
              setDayDetail(undefined);
              handleClickScheduleItem(schedule);
            }}
            data={dayDetail?.schedules ?? []}
          />
        </DayDetailList>
      </Modal>
    </WideDefaultLayout>
  );
};

export default ScheduleIndex;

/* ─── Styles ─── */

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  width: 100%;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.app.border};
  background: ${({ theme }) => theme.app.bg.white};
  overflow: visible;

  ${({ theme }) => theme.medias.max600} {
    padding: 12px 8px;
    border-radius: 10px;
  }
`;

const TopSearchBar = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 16px;

  ${({ theme }) => theme.medias.max600} {
    margin-bottom: 10px;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.app.text.light2};
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 36px 10px 36px;
  border: 1px solid ${({ theme }) => theme.app.border};
  border-radius: 12px;
  background: ${({ theme }) => theme.app.bg.main};
  color: ${({ theme }) => theme.app.text.black};
  font-size: 15px;
  outline: none;
  transition: border-color 0.2s;

  &::placeholder {
    color: ${({ theme }) => theme.app.text.light2};
  }

  &:focus {
    border-color: ${({ theme }) => theme.app.text.light1};
  }

  ${({ theme }) => theme.medias.max900} {
    font-size: 14px;
    padding: 8px 32px 8px 32px;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  border: none;
  background: none;
  color: ${({ theme }) => theme.app.text.light2};
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  line-height: 1;
`;

const Controller = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 24px;

  strong {
    font-size: 22px;
    font-weight: 700;
    color: ${({ theme }) => theme.app.text.black};

    ${({ theme }) => theme.medias.max900} {
      font-size: 18px;
    }

    ${({ theme }) => theme.medias.max600} {
      font-size: 16px;
    }
  }

  p {
    ${({ theme }) => theme.medias.max600} {
      font-size: 12px !important;
    }
  }

  button {
    width: 40px;
    height: 40px;
    background: url(${ArrowIcon}) no-repeat center / 100%;

    ${({ theme }) => theme.medias.max900} {
      width: 30px;
      height: 30px;
    }

    ${({ theme }) => theme.medias.max600} {
      width: 24px;
      height: 24px;
    }

    &.next {
      transform: rotate(180deg);
    }
  }

  ${({ theme }) => theme.medias.max600} {
    gap: 12px;
  }
`;

const HeaderGroup = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  align-items: center;

  ${({ theme }) => theme.medias.max900} {
    margin-top: 5px;
  }

  ${({ theme }) => theme.medias.max600} {
    flex-direction: column;
    gap: 8px;
  }
`;

const Filters = styled.div`
  display: flex;
  gap: 6px;

  ${({ theme }) => theme.medias.max900} {
    margin-top: 5px;
    align-items: center;
  }

  ${({ theme }) => theme.medias.max600} {
    width: 100%;
    justify-content: center;
    margin-top: 0;
  }
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;

  ${({ theme }) => theme.medias.max900} {
    justify-content: center;
    margin-top: 10px;
  }

  button {
    ${({ theme }) => theme.medias.max900} {
      zoom: 0.5;
    }
  }

  span {
    ${({ theme }) => theme.medias.max900} {
      font-size: 10px;
    }
  }

  ${({ theme }) => theme.medias.max600} {
    width: 100%;
    justify-content: center;
    margin-top: 0;

    button {
      zoom: 0.7;
    }
  }
`;

const filterButtonCss = (isActive: boolean) => css`
  padding: 3px 16px;
  border-radius: 18px;
  color: ${({ theme }) =>
    isActive ? theme.app.palette.gray[0] : theme.app.text.light1};
  background: ${({ theme }) =>
    isActive ? theme.app.palette.gray[800] : theme.app.bg.gray1};

  ${({ theme }) => theme.medias.max900} {
    padding: 2px 12px;
  }

  ${({ theme }) => theme.medias.max600} {
    padding: 1px 8px;
  }
`;

const MonthGrid = styled.div`
  display: grid;
  column-gap: 6px;
  row-gap: 12px;
  margin-top: 24px;
  margin-bottom: 16px;
  width: 100%;
  text-align: center;
  grid-template-columns: repeat(7, 1fr);

  ${({ theme }) => theme.medias.max900} {
    margin-top: 8px;
    margin-bottom: 10px;
    column-gap: 3px;
    row-gap: 8px;
  }

  ${({ theme }) => theme.medias.max600} {
    column-gap: 2px;
    row-gap: 6px;
    margin-top: 6px;
  }
`;

const WeekdayHeader = styled.div<{ $index: number; $showWen: boolean }>`
  position: sticky;
  top: 70px;
  z-index: 10;
  font-weight: bold;
  padding: 6px 0;
  border-radius: 6px;
  background: ${({ theme }) => theme.app.bg.reverse};
  color: ${({ $index, $showWen, theme }) => {
    if ($showWen) {
      if ($index === 4) return theme.app.palette.red[250];
      if ($index === 3) return theme.app.palette.blue[350];
    } else {
      if ($index === 0) return theme.app.palette.red[250];
      if ($index === 6) return theme.app.palette.blue[350];
    }
    return theme.app.text.reverse;
  }};

  ${({ theme }) => theme.medias.max900} {
    font-size: 10px;
    padding: 4px 0;
  }
`;

const DateCell = styled.div<{
  $weekday?: number;
  $isToday?: boolean;
  $isPrevOrNext?: boolean;
  $showWen?: boolean;
}>`
  display: flex;
  flex-direction: column;
  min-height: 160px;
  border-radius: 8px;
  border: 1px solid
    ${({ $isToday, theme }) =>
      $isToday ? theme.app.bg.reverse : theme.app.border};
  box-shadow: ${({ $isToday }) =>
    $isToday ? "0 0 8px rgba(0, 0, 0, 0.12)" : "none"};
  opacity: ${({ $isPrevOrNext }) => ($isPrevOrNext ? 0.4 : 1)};
  overflow: hidden;
  background: ${({ theme }) => theme.app.bg.white};

  ${({ theme }) => theme.medias.max900} {
    min-height: 80px;
  }

  ${({ theme }) => theme.medias.max600} {
    min-height: 60px;
    border-radius: 4px;
  }
`;

const DateHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 6px;
  border-bottom: 1px solid ${({ theme }) => theme.app.border};
  background: ${({ theme }) => theme.app.bg.main};

  ${({ theme }) => theme.medias.max900} {
    padding: 2px 4px;
  }

  ${({ theme }) => theme.medias.max600} {
    padding: 2px 3px;
    justify-content: center;
  }
`;

const DateNumber = styled.span<{
  $isToday?: boolean;
  $isPrevOrNext?: boolean;
  $weekday?: number;
  $showWen?: boolean;
}>`
  position: relative;
  font-size: 14px;
  font-weight: 600;
  color: ${({ $isPrevOrNext, $weekday, $showWen, theme }) => {
    if ($isPrevOrNext) return theme.app.text.light1;
    if ($showWen) {
      if ($weekday === 4) return theme.app.palette.red[250];
      if ($weekday === 3) return theme.app.palette.blue[350];
    } else {
      if ($weekday === 0) return theme.app.palette.red[250];
      if ($weekday === 6) return theme.app.palette.blue[350];
    }
    return theme.app.text.black;
  }};

  ${({ $isToday, theme }) =>
    $isToday &&
    css`
      &::after {
        content: "";
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: ${theme.app.bg.reverse};
        opacity: 0.15;
        pointer-events: none;

        ${theme.medias.max900} {
          width: 18px;
          height: 18px;
        }
      }
    `}

  ${({ theme }) => theme.medias.max900} {
    font-size: 10px;
  }
`;

const Profit = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.app.palette.blue[350]};
  font-weight: 600;
  white-space: nowrap;

  ${({ theme }) => theme.medias.max900} {
    font-size: 8px;
  }

  ${({ theme }) => theme.medias.max600} {
    display: none;
  }
`;

const ScheduleArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 4px 10px;
  flex: 1;

  ${({ theme }) => theme.medias.max900} {
    padding: 4px 2px 8px;
    gap: 3px;
  }

  ${({ theme }) => theme.medias.max600} {
    padding: 3px 1px 6px;
    gap: 2px;
  }
`;

const CompactItem = styled.button<{ $isAlone: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 4px;
  width: 100%;
  text-align: left;
  cursor: pointer;
  border: none;
  background: none;
  border-left: 3px solid
    ${({ $isAlone, theme }) =>
      $isAlone ? theme.app.palette.red[250] : theme.app.palette.blue[350]};
  border-radius: 0 4px 4px 0;
  transition: background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.app.bg.gray1};
  }

  ${({ theme }) => theme.medias.max900} {
    padding: 1px 3px;
    gap: 2px;
  }

  ${({ theme }) => theme.medias.max600} {
    padding: 1px 2px;
    border-left-width: 2px;
    border-radius: 0 2px 2px 0;
  }
`;

const CompactTime = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.app.text.light2};
  flex-shrink: 0;

  ${({ theme }) => theme.medias.max900} {
    font-size: 8px;
  }

  ${({ theme }) => theme.medias.max600} {
    display: none;
  }
`;

const CompactName = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.app.text.main};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${({ theme }) => theme.medias.max900} {
    font-size: 9px;
  }

  ${({ theme }) => theme.medias.max600} {
    font-size: 8px;
  }
`;

const MoreCount = styled.button`
  padding: 2px 4px;
  font-size: 10px;
  font-weight: 600;
  color: ${({ theme }) => theme.app.text.light2};
  text-align: left;
  cursor: pointer;
  border: none;
  background: none;
  margin-top: 2px;

  &:hover {
    color: ${({ theme }) => theme.app.text.main};
  }

  ${({ theme }) => theme.medias.max900} {
    font-size: 8px;
    padding: 1px 3px;
  }

  ${({ theme }) => theme.medias.max600} {
    font-size: 9px;
    padding: 4px 2px;
    text-align: center;
    width: 100%;
    color: ${({ theme }) => theme.app.text.light1};
  }
`;

const DayDetailList = styled.ul`
  display: flex;
  flex-direction: column;
  min-width: 280px;
`;

const SwitchWrapper = styled.button<{ $isOn: boolean }>`
  width: 50px;
  height: 25px;
  background: ${({ $isOn }) => ($isOn ? "#4CAF50" : "#F44336")};
  border-radius: 50px;
  position: relative;
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  padding: 2px;
  transition: background 0.3s ease-in-out;
`;

const SwitchSlider = styled.div<{ $isOn: boolean }>`
  width: 20px;
  height: 20px;
  background: ${({ theme }) => theme.app.bg.reverse};
  border-radius: 50%;
  position: absolute;
  left: ${({ $isOn }) => ($isOn ? "calc(100% - 22px)" : "3px")};
  transition: left 0.3s ease-in-out;
`;

const CalendarSwitchContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 15px;
`;
